import React, { useMemo, useState } from 'react';
import { apiGet, apiPatch, apiPost } from '../../api';
import CacEvolutionSection from '../../components/CacEvolutionSection';
import KpiCard from '../../components/KpiCard';
import RetentionSummarySection from '../../components/RetentionSummarySection';
import SimpleBarChart from '../../components/SimpleBarChart';
import StaticKpiCard from '../../components/StaticKpiCard';
import { useToast } from '../../components/ToastHost';
import { useAsync } from '../../hooks/useAsync';
import type { KpisResponse, StaticKpi } from '../../types';

export default function AdminKpis() {
  const { data: kpis, loading, error } = useAsync<KpisResponse>(() => apiGet('/api/admin/kpis'), []);
  const { data: staticList, loading: loadingStatic, error: errStatic, setData: setStaticList } = useAsync<{ items: StaticKpi[] }>(
    () => apiGet('/api/admin/static-kpis'),
    []
  );
  const toast = useToast();

  const [title, setTitle] = useState('');
  const [kpiType, setKpiType] = useState<StaticKpi['kpiType']>('number');
  const [valueText, setValueText] = useState('');
  const [description, setDescription] = useState('');
  const [placement, setPlacement] = useState<'header' | '2025' | '2026'>('header');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<StaticKpi & { placement: 'header' | '2025' | '2026' }> | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const mergedStatic = useMemo(() => {
    const fromList = staticList?.items || [];
    return fromList;
  }, [staticList]);

  const create = async () => {
    setCreating(true);
    try {
      await apiPost('/api/admin/static-kpis', { title, kpiType, valueText, description: description || null, placement });
      setTitle('');
      setValueText('');
      setDescription('');
      setStaticList(await apiGet('/api/admin/static-kpis'));
      toast.success('KPI added.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not add KPI.');
      throw e;
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete KPI?')) return;
    try {
      await fetch(`/api/admin/static-kpis/${id}`, { method: 'DELETE', credentials: 'include' });
      setStaticList(await apiGet('/api/admin/static-kpis'));
      toast.success('KPI deleted.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not delete KPI.');
      throw e;
    }
  };

  if (loading) return <div className="card">Loading metrics…</div>;
  if (error || !kpis) return <div className="card">Couldn't load metrics: {error || 'unknown'}</div>;

  const k = kpis.kpis;
  const r25 = kpis.yearReview?.review2025;
  const y26 = kpis.yearReview?.live2026;
  const funnelSnapshot = (kpis.funnel?.snapshot || []).filter((x) => x.stage !== 'Perdido' && x.stage !== 'Ganado');
  const funnelSnapshotDisplay = funnelSnapshot.map((x) => (x.stage === 'Prospección' ? { ...x, stage: '1ra Reunión' } : x));
  const funnelConversionDisplay = (kpis.funnel?.conversion || []).map((x) => (x.stage === 'Prospección' ? { ...x, stage: '1ra Reunión' } : x));

  const headerStatic = (kpis.staticKpis || []).filter((x) => (x as any).placement == null || (x as any).placement === 'header');
  const static2025 = (kpis.staticKpis || []).filter((x) => (x as any).placement === '2025');
  const static2026 = (kpis.staticKpis || []).filter((x) => (x as any).placement === '2026');
  const startEdit = (item: StaticKpi) => {
    setEditingId(item.id);
    setEditDraft({
      title: item.title,
      kpiType: item.kpiType,
      valueText: item.valueText,
      description: item.description || '',
      placement: (item as any).placement || 'header',
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };
  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    setSavingEdit(true);
    try {
      await apiPatch(`/api/admin/static-kpis/${editingId}`, {
        title: editDraft.title,
        kpiType: editDraft.kpiType,
        valueText: editDraft.valueText,
        description: editDraft.description || null,
        placement: (editDraft as any).placement,
      });
      setStaticList(await apiGet('/api/admin/static-kpis'));
      cancelEdit();
      toast.success('KPI updated.');
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not update KPI.');
      throw e;
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>Metrics Dashboard</div>
            <div className="muted" style={{ fontSize: 12 }}>
              Updated: {new Date(kpis.computedAt).toLocaleString()}
            </div>
          </div>
          <a className="btn" href="/app/kpis" target="_blank" rel="noreferrer">
            Open investor portal
          </a>
        </div>
      </div>

      <div className="grid kpis">
        <KpiCard title="% Win Rate (1st Meeting to Contract)" value={k.winRateP2P} format="percentage" />
        {headerStatic.map((kpi) => (
          <StaticKpiCard key={kpi.id} kpi={kpi as any} />
        ))}
      </div>

      <CacEvolutionSection />

      <RetentionSummarySection retention={kpis.retentionSummary} detailHref="/admin/kpis/retention" />

      {r25 ? (
        <div className="grid" style={{ gap: 14 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>2025 Review</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Bookings & pipeline creation (2025)
                </div>
              </div>
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <KpiCard title="Bookings" value={r25.bookings} format="currency" />
              <KpiCard title="Sales Cycle (days)" value={r25.cycleDays} format="days" />
              <KpiCard title="Pipeline Generation" value={(r25 as any).pipelineGeneration ?? (r25.pipelineCreationByQuarter || []).reduce((s, x) => s + (x.amount || 0), 0)} format="currency" />
            </div>
            <div style={{ height: 12 }} />
            {static2025.length ? (
              <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                {static2025.map((kpi) => (
                  <StaticKpiCard key={kpi.id} kpi={kpi as any} />
                ))}
              </div>
            ) : null}
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SimpleBarChart title="Bookings by quarter (2025)" items={r25.bookingsByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
              <SimpleBarChart title="Pipeline creation by quarter (2025)" items={r25.pipelineCreationByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
            </div>
          </div>
        </div>
      ) : null}

      {y26 ? (
        <div className="grid" style={{ gap: 14 }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>2026 Live Data</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  As of {y26.endYmd || new Date(kpis.computedAt).toISOString().slice(0, 10)}
                </div>
              </div>
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              <KpiCard title="Bookings" value={y26.bookings} format="currency" />
              <KpiCard title="Sales Cycle (days)" value={y26.cycleDays} format="days" />
              <KpiCard title="Open Pipeline" value={y26.openPipeline} format="currency" />
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              <KpiCard title="New Clients" value={y26.clientsCount} format="number" />
              {static2026.map((kpi) => (
                <StaticKpiCard key={kpi.id} kpi={kpi as any} />
              ))}
            </div>
            <div style={{ height: 12 }} />
            <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <SimpleBarChart title="Bookings by quarter (2026)" items={y26.bookingsByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
              <SimpleBarChart title="Pipeline creation by quarter (2026)" items={y26.pipelineCreationByQuarter} valueKey="amount" labelKey="quarter" format="currency" />
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <SimpleBarChart title="Pipeline funnel ($)" items={funnelSnapshotDisplay} valueKey="amount" labelKey="stage" format="currency" />
        <SimpleBarChart title="Funnel conversion (%)" items={funnelConversionDisplay} valueKey="convFromPrev" labelKey="stage" format="percentage" />
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div className="card">
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 8 }}>Add static KPI</div>
          {errStatic ? <div className="muted">Error loading static KPIs: {errStatic}</div> : null}
          <div className="row">
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Title
              </div>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: ARR run-rate" />
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Type
              </div>
              <select className="select" value={kpiType} onChange={(e) => setKpiType(e.target.value as any)}>
                <option value="currency">Currency</option>
                <option value="number">Number</option>
                <option value="percentage">Percentage</option>
                <option value="days">Days</option>
                <option value="text">Text</option>
              </select>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Placement
              </div>
                <select className="select" value={placement} onChange={(e) => setPlacement(e.target.value as any)}>
                  <option value="header">Header</option>
                  <option value="2025">In 2025</option>
                  <option value="2026">In 2026</option>
                </select>
            </div>
          </div>
          <div style={{ height: 10 }} />
          <div className="row">
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Value
              </div>
              <input className="input" value={valueText} onChange={(e) => setValueText(e.target.value)} placeholder={kpiType === 'percentage' ? 'Ex: 42.5' : 'Ex: 100000'} />
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                Description (optional)
              </div>
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short context" />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn primary" disabled={creating || !title.trim() || !valueText.trim()} onClick={create}>
                {creating ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
          <div style={{ height: 12 }} />
          <div className="muted" style={{ fontSize: 12 }}>
            Note: for numeric types the value must be a number (percentage is 0–100).
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 900 }}>Static KPIs ({mergedStatic.length})</div>
              <div className="muted" style={{ fontSize: 12 }}>
                Shown in the investor portal (Header / 2025 / 2026)
              </div>
            </div>
          </div>
          <div className="hr" />
          {loadingStatic ? (
            <div className="muted">Loading…</div>
          ) : !mergedStatic.length ? (
            <div className="muted">No static KPIs yet.</div>
          ) : (
            <div className="grid" style={{ gap: 12 }}>
              {mergedStatic.map((sk) => {
                const isEditing = editingId === sk.id;
                const skPlacement = (sk as any).placement || 'header';
                return (
                  <div key={sk.id} style={{ border: '1px solid var(--border)', borderRadius: 14, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div className="pill">{skPlacement === 'header' ? 'Header' : skPlacement}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {sk.kpiType}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10 }}>
                        {isEditing ? (
                          <>
                            <button
                              className="btn primary"
                              onClick={saveEdit}
                              disabled={savingEdit || !editDraft?.title?.trim() || !editDraft?.valueText?.trim()}
                            >
                              {savingEdit ? 'Saving…' : 'Save'}
                            </button>
                            <button className="btn" onClick={cancelEdit}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn" onClick={() => startEdit(sk)}>
                              Edit
                            </button>
                            <button className="btn danger" onClick={() => remove(sk.id)}>
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <div style={{ height: 10 }} />
                    <StaticKpiCard kpi={sk as any} />

                    {isEditing && editDraft ? (
                      <>
                        <div style={{ height: 10 }} />
                        <div className="row">
                          <div>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                              Title
                            </div>
                            <input className="input" value={String(editDraft.title || '')} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} />
                          </div>
                          <div>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                              Type
                            </div>
                            <select className="select" value={String(editDraft.kpiType || 'number')} onChange={(e) => setEditDraft({ ...editDraft, kpiType: e.target.value as any })}>
                              <option value="currency">Currency</option>
                              <option value="number">Number</option>
                              <option value="percentage">Percentage</option>
                              <option value="days">Days</option>
                              <option value="text">Text</option>
                            </select>
                          </div>
                          <div>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                              Placement
                            </div>
                            <select
                              className="select"
                              value={String((editDraft as any).placement || 'header')}
                              onChange={(e) => setEditDraft({ ...editDraft, placement: e.target.value as any } as any)}
                            >
                              <option value="header">Header</option>
                              <option value="2025">In 2025</option>
                              <option value="2026">In 2026</option>
                            </select>
                          </div>
                        </div>
                        <div style={{ height: 10 }} />
                        <div className="row">
                          <div>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                              Value
                            </div>
                            <input className="input" value={String(editDraft.valueText || '')} onChange={(e) => setEditDraft({ ...editDraft, valueText: e.target.value })} />
                          </div>
                          <div>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                              Description
                            </div>
                            <input className="input" value={String(editDraft.description || '')} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} />
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
